import { styled } from "@mui/material/styles";

import type { Hbml } from "@holibob-packages/hbml";

import { Avatar } from "../../../components/Avatar";
import { Typography } from "../../../components/Typography";
import { useNextTranslation } from "../../../hooks";
import { ProductContentHbmlRenderer } from "../ProductContentHbmlRenderer";
import { ProductSectionTitle } from "../ProductSectionTitle";
import { ProductSectionWrapper, ProductSectionWrapperProps } from "./ProductSectionWrapper";

type ProductOverviewSectionProps = {
    descriptionJson: Hbml;
    providerName?: string;
    sourceInstanceName?: string;
    showOperatedBy: boolean;
    showSourcedFrom: boolean;
} & Omit<ProductSectionWrapperProps, "children">;

export const ProductOverviewSection = ({
    descriptionJson,
    providerName,
    sourceInstanceName,
    showOperatedBy,
    showSourcedFrom,
    ...props
}: ProductOverviewSectionProps) => {
    const [t] = useNextTranslation("product");

    const productSourcesGenerator = useProductSources({
        showOperatedBy,
        showSourcedFrom,
        providerName,
        sourceInstanceName,
    });
    const productSources = Array.from(productSourcesGenerator);

    return (
        <ProductSectionWrapper {...props} data-testid="product-overview-section">
            <ProductSectionTitle data-ref="product-overview-section-title">{t("overview.title")}</ProductSectionTitle>
            <ProductContentHbmlRenderer content={descriptionJson} />
            {productSources.length > 0 && (
                <ProductSourceGroupWrapper data-testid="product-overview-source">
                    {productSources.map((productSource) => (
                        <ProductSource key={productSource.name} {...productSource} />
                    ))}
                </ProductSourceGroupWrapper>
            )}
        </ProductSectionWrapper>
    );
};

function* useProductSources(
    params: Pick<
        ProductOverviewSectionProps,
        "showOperatedBy" | "showSourcedFrom" | "providerName" | "sourceInstanceName"
    >
) {
    const { showOperatedBy, showSourcedFrom, providerName, sourceInstanceName } = params;

    const [t] = useNextTranslation("product");

    if (showOperatedBy && providerName) {
        yield {
            label: t("provider.operatedBy"),
            name: providerName,
        };
    }

    if (showSourcedFrom && sourceInstanceName) {
        yield {
            label: t("provider.sourcedFrom"),
            name: sourceInstanceName,
        };
    }
}

function ProductSource({ label, name }: { label: string; name: string }) {
    return (
        <ProductSourceItemWrapper>
            <Avatar name={name} size="medium" />
            <div>
                <Typography color="subtle">{label}</Typography>
                <Typography>{name}</Typography>
            </div>
        </ProductSourceItemWrapper>
    );
}

const ProductSourceItemWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    wordBreak: "break-all",
}));

const ProductSourceGroupWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
}));
